// frontend/src/pages/Subscription.jsx
import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";

const USER_KEY = "aifine_user";

function safeParse(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function readTier() {
  const obj = safeParse(localStorage.getItem(USER_KEY), null);
  const tier = obj?.subscription_tier ?? obj?.subscriptionTier ?? obj?.tier ?? null;
  return tier ? String(tier) : "single";
}

function writeTier(nextTier) {
  const clean = String(nextTier || "").toLowerCase() === "top" ? "top" : "single";
  const obj = {
    subscription_tier: clean,
    updated_at_iso: new Date().toISOString(),
  };
  localStorage.setItem(USER_KEY, JSON.stringify(obj));
  return clean;
}

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

export default function Subscription() {
  const navigate = useNavigate();
  const [tier, setTier] = useState(() => readTier());
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const isTop = useMemo(() => String(tier).toLowerCase() === "top", [tier]);

  function setStandard() {
    if (!acceptedTerms) return;
    const t = writeTier("single");
    setTier(t);
    navigate("/start-audit?mode=rerun");
  }

  function setTop() {
    if (!acceptedTerms) return;
    const t = writeTier("top");
    setTier(t);
    navigate("/start-audit?mode=new");
  }

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-4xl px-8 py-10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-black tracking-tight">Subscription</h1>
              <p className="mt-1 text-sm text-slate-600">
                MVP testing controls (UI-based) for tier behavior. No devtools required.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs">
              <div className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">
                Current tier
              </div>
              <div className="mt-1 font-black text-slate-900">
                {isTop ? "Top (Multi-entity)" : "Standard (Single-entity)"}
              </div>
            </div>
          </div>

          <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-700">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
              />
              <span className="leading-6">
                I have read and agree to the{" "}
                <Link to="/terms" className="font-black text-slate-900 underline underline-offset-2">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/legal" className="font-black text-slate-900 underline underline-offset-2">
                  Legal Disclaimer
                </Link>.
              </span>
            </label>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={setStandard}
              disabled={!acceptedTerms}
              className={cn(
                "rounded-3xl border p-6 text-left shadow-sm transition-colors",
                !isTop ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:bg-slate-50",
                !acceptedTerms && "cursor-not-allowed opacity-60"
              )}
            >
              <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">
                Standard
              </div>
              <div className="mt-2 text-xl font-black text-slate-900">Single-entity</div>
              <div className="mt-2 text-sm text-slate-600">
                “New Audit” routes to <span className="font-black">/start-audit?mode=rerun</span>. No chooser.
              </div>
              <div className="mt-4 inline-flex rounded-2xl bg-slate-900 px-4 py-2 text-xs font-black text-white">
                Activate Standard
              </div>
            </button>

            <button
              type="button"
              onClick={setTop}
              disabled={!acceptedTerms}
              className={cn(
                "rounded-3xl border p-6 text-left shadow-sm transition-colors",
                isTop ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:bg-slate-50",
                !acceptedTerms && "cursor-not-allowed opacity-60"
              )}
            >
              <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">
                Top
              </div>
              <div className="mt-2 text-xl font-black text-slate-900">Multi-entity</div>
              <div className="mt-2 text-sm text-slate-600">
                “New Audit” routes to <span className="font-black">/start-audit</span> and defaults to{" "}
                <span className="font-black">?mode=new</span> (chooser + blank profile).
              </div>
              <div className="mt-4 inline-flex rounded-2xl bg-slate-900 px-4 py-2 text-xs font-black text-white">
                Activate Top
              </div>
            </button>
          </div>

          {!acceptedTerms ? (
            <div className="mt-4 text-xs font-bold text-slate-500">
              Accept the Terms of Service and Legal Disclaimer before activating a subscription tier.
            </div>
          ) : null}

          <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-700">
            <div className="font-black text-slate-900">How to test (no storage editing)</div>
            <ol className="mt-3 list-decimal pl-5 space-y-2">
              <li>Go to <span className="font-black">/subscription</span>.</li>
              <li>Check the acceptance box.</li>
              <li>Click <span className="font-black">Activate Standard</span> and confirm “New Audit” goes to rerun.</li>
              <li>Return to <span className="font-black">/subscription</span>.</li>
              <li>Click <span className="font-black">Activate Top</span> and confirm chooser + new mode appear.</li>
            </ol>
          </div>

          <div className="mt-10 pb-8 text-xs text-slate-400">
            © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
          </div>
        </div>
      </main>
    </div>
  );
}
